/**
 * Thin fetch wrapper for the API.
 *
 * The session lives in an HttpOnly cookie the browser sends automatically, so
 * there is no token to attach here. What we must attach is the antiforgery
 * token on every state-changing request, and we must recognise the two
 * distinct 401 shapes the API produces: "you are not signed in" (send the user
 * to sign in) and "your access was revoked" (send them to a dead end).
 */

export class ApiError extends Error {
  readonly status: number
  readonly detail: string
  readonly problem?: ProblemDetails

  constructor(status: number, detail: string, problem?: ProblemDetails) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.problem = problem
  }
}

export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  errors?: Record<string, string[]>
}

const ANTIFORGERY_COOKIE = 'XSRF-TOKEN'
const ANTIFORGERY_HEADER = 'X-XSRF-TOKEN'

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1]
}

type UnauthorizedHandler = (reason: 'unauthenticated' | 'revoked') => void

let onUnauthorized: UnauthorizedHandler = () => {}

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Set for multipart uploads, where the browser must build the boundary itself. */
  rawBody?: BodyInit
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, rawBody, headers, method = 'GET', ...rest } = options
  const isMutation = method !== 'GET' && method !== 'HEAD'

  const finalHeaders = new Headers(headers)
  if (body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json')
  }
  if (isMutation) {
    const token = readCookie(ANTIFORGERY_COOKIE)
    if (token) {
      finalHeaders.set(ANTIFORGERY_HEADER, decodeURIComponent(token))
    }
  }

  const response = await fetch(path.startsWith('/') ? path : `/api/${path}`, {
    ...rest,
    method,
    headers: finalHeaders,
    credentials: 'same-origin',
    body: rawBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
  })

  if (response.status === 401) {
    const problem = await readProblem(response)
    const revoked = problem?.type?.endsWith('access-revoked') ?? false
    onUnauthorized(revoked ? 'revoked' : 'unauthenticated')
    throw new ApiError(401, problem?.detail ?? 'Not signed in.', problem)
  }

  if (!response.ok) {
    const problem = await readProblem(response)
    throw new ApiError(
      response.status,
      problem?.detail ?? problem?.title ?? `Request failed (${response.status}).`,
      problem,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.includes('application/json')) {
    return undefined as T
  }

  return (await response.json()) as T
}

async function readProblem(response: Response): Promise<ProblemDetails | undefined> {
  try {
    const contentType = response.headers.get('Content-Type') ?? ''
    if (!contentType.includes('json')) {
      return undefined
    }
    return (await response.json()) as ProblemDetails
  } catch {
    return undefined
  }
}
